import { LitElement, html, css, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { ShouldUpdate } from '../../../modules/ShouldUpdate.js';
import { map } from 'lit-html/directives/map.js';
import { when } from 'lit/directives/when.js';
import lightTheme from '../../../themes/light.styles.js';
import '@lit-labs/virtualizer';
import SlSelect from '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import './model-item-tag.js';
import '../../katapult-elements/katapult-dialog.js';
import '../../katapult-elements/katapult-button.js';
import '@lit-labs/virtualizer';
import { DEFAULT_TAGS, DEFAULT_ITEM_TAG_MAP } from './DefaultTags.js';
import './create-tag-dialog.js';

export class ModelItemTagSelector extends LitElement {
  static properties = {
    modelPath: { type: String, shouldUpdate: false },
    modelCategory: { type: String },
    itemKey: { type: String },
    tags: { type: Array },
    _modelTags: { type: Object, shouldUpdate: false },
    _allSelectableTags: { type: Object },
    lookLikePaperElement: { type: Boolean, attribute: 'look-like-paper-element', reflect: true },
    maySelectDefaultTags: { type: Boolean },
    mayAddTags: { type: Boolean },
    maxOptionsVisible: { type: Number }
  };

  static styles = [
    lightTheme,
    css`
      :host {
        display: contents;
      }
      sl-select {
        width: inherit;
      }
      sl-menu-item::part(label) {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--sl-spacing-2x-small);
      }
      sl-select::part(expand-icon) {
        color: var(--paper-grey-800);
      }
      sl-option[disabled] {
        display: none;
      }
    `
  ];

  render() {
    const allTagsArray = Object.entries(this._allSelectableTags ?? {}).map(([_id, tag]) => ({ ...tag, _id }));

    // Get hardcoded tags, dedupe, and filter out any that are missing a config
    const hardcodedTags = DEFAULT_ITEM_TAG_MAP[this.modelCategory]?.[this.itemKey] ?? [];
    const itemTags = [...new Set([...(this.tags ?? []), ...hardcodedTags])];
    const filteredItemTags = itemTags.filter((tagId) => allTagsArray.some((tag) => tag._id === tagId));

    const defaultTagIds = Object.keys(DEFAULT_TAGS);
    return html`
      <create-tag-dialog ${ref(this._createTagsDialog)} .modelPath=${this.modelPath}></create-tag-dialog>
      <sl-select
        ?look-like-paper-element=${this.lookLikePaperElement}
        @sl-input=${this.#inputHandler.bind(this)}
        .maxOptionsVisible=${this.maxOptionsVisible}
        .value=${filteredItemTags.join(' ')}
        .getTag=${(option) => {
          const tagId = option.getAttribute('value');
          const tag = this._allSelectableTags[tagId];
          return html`<model-item-tag .tag=${tag} .removable=${!defaultTagIds.includes(tagId)}></model-item-tag>`;
        }}
        multiple
        placeholder="Select tags"
        hoist
      >
        ${map(
          allTagsArray,
          (tag) => html`
            <sl-option value=${tag._id} .disabled=${!this.maySelectDefaultTags && defaultTagIds.includes(tag._id)}>
              <model-item-tag .tag=${tag}></model-item-tag>
            </sl-option>
          `
        )}
        ${when(
          this.mayAddTags,
          () => html`
            <sl-divider></sl-divider>
            <sl-menu-item @click=${() => this._createTagsDialog?.value?.open()}>
              <katapult-icon icon="create" size="20"></katapult-icon>
              <span>Add a Tag</span>
            </sl-menu-item>
          `
        )}
      </sl-select>
    `;
  }

  constructor() {
    super();
    /** @type {?string} */
    this.modelPath = null;
    /** @type {?string} */
    this.modelCategory = null;
    /** @type {?string} */
    this.itemKey = null;
    /** @type {Array} */
    this.tags = [];
    /** @type {Object} */
    this._modelTags = null;
    /** @type {?Function} */
    this._disconnectTagsListener = null;
    this._createTagsDialog = createRef();
    /** @type {Boolean} */
    this.lookLikePaperElement = false;
    /** @type {Boolean} */
    this.maySelectDefaultTags = false;
    /** @type {SlSelect['maxOptionsVisible']} */
    this.maxOptionsVisible = 0;
    /** @type {Boolean} */
    this.mayAddTags = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectTagsListener?.();
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('modelPath')) this.#updateModelTagsListener();
    if (changedProperties.has('_modelTags')) this._allSelectableTags = { ...this._modelTags, ...DEFAULT_TAGS };
    return ShouldUpdate(this, changedProperties);
  }

  #updateModelTagsListener() {
    this._disconnectTagsListener?.();
    if (!this.modelPath) return;

    const modelTagsRef = globalThis.FirebaseWorker.ref(this.modelPath).child('models/tags');
    const callback = (snapshot) => (this._modelTags = snapshot.val());

    modelTagsRef.on('value', callback);
    this._disconnectTagsListener = () => {
      modelTagsRef.off('value', callback);
      this._modelTags = null;
      this._disconnectTagsListener = null;
    };
  }

  #inputHandler(e) {
    const selectedTagIds = e.target.value;
    const filteredTagIds = selectedTagIds.filter((tagId) => {
      if (tagId == '') return false;
      // Exclude default tags from the final array unless they are allowed to be selected.
      if (!this.maySelectDefaultTags && DEFAULT_TAGS[tagId] != null) return false;
      const tag = this._allSelectableTags[tagId];
      if (!tag) return false;
      return true;
    });
    this.tags = filteredTagIds;
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: { tags: this.tags }
      })
    );
  }
}
customElements.define('model-item-tag-selector', ModelItemTagSelector);
